/**
 * PauseAndGoList - QuetureTestPage용 PauseAndGo 목록 컴포넌트
 *
 * 영상 타임라인 시각화 + 테이블 형식으로 모든 PauseAndGo 항목과
 * 진행 상태(완료/미완료, 결과)를 표시한다.
 */
import { CheckCircle, Circle } from 'lucide-react'
import type { PauseAndGoItem, PauseAndGoProgress, QuetureTestLabels } from './types'
import { PAUSE_AND_GO_TYPE_LABELS, PAUSE_AND_GO_TYPE_COLORS, formatTime } from './constants'

// ---------------------------------------------------------------------------
// Dot color map for the timeline bar (bg only, one solid color per type)
// ---------------------------------------------------------------------------
const TIMELINE_DOT_COLORS: Record<string, string> = {
  PAUSE_AND_GO: 'bg-blue-500',
  PAUSE_AND_AUTOGO: 'bg-green-500',
  PAUSE_AND_REPEAT: 'bg-yellow-500',
  PAUSE_AND_QUIZ: 'bg-purple-500',
  PAUSE_AND_VIDEO: 'bg-pink-500',
}

// ---------------------------------------------------------------------------
// Result label map
// ---------------------------------------------------------------------------
const RESULT_LABELS: Record<string, string> = {
  correct: '정답',
  incorrect: '오답',
  yes: '예',
  no: '아니오',
  confirmed: '확인',
  auto: '자동',
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface PauseAndGoListProps {
  pauseAndGos: PauseAndGoItem[]
  progress: PauseAndGoProgress
  currentBundleItem: 'main' | 'summary'
  onSeekTo: (timeMs: number) => void
  labels: QuetureTestLabels
  videoDuration?: number
}

// ---------------------------------------------------------------------------
// Helper: truncate message string
// ---------------------------------------------------------------------------
function truncate(str: string, maxLen: number): string {
  return str.length > maxLen ? str.slice(0, maxLen) + '…' : str
}

// ---------------------------------------------------------------------------
// Helper: derive display message for a PauseAndGo item
// ---------------------------------------------------------------------------
function getDisplayMessage(pag: PauseAndGoItem): string {
  if (pag.type === 'PAUSE_AND_QUIZ' && pag.quizId) {
    return truncate(pag.quizId, 30)
  }
  if (pag.type === 'PAUSE_AND_VIDEO' && pag.videoKey) {
    return truncate(pag.videoKey, 30)
  }
  if (pag.msg) {
    return truncate(pag.msg, 30)
  }
  return '-'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function PauseAndGoList({
  pauseAndGos,
  progress,
  currentBundleItem,
  onSeekTo,
  labels,
  videoDuration,
}: PauseAndGoListProps) {
  const totalMs = videoDuration ? videoDuration * 1000 : 0

  const completedCount = pauseAndGos.filter((pag) => progress[pag.id]?.completed).length
  const totalCount = pauseAndGos.length

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-2xs space-y-4">
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                              */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {labels.pauseAndGoList}
        </h2>
        <span className="text-sm font-medium text-gray-500">
          {completedCount} / {totalCount} {labels.completed}
        </span>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Timeline bar                                                        */}
      {/* ------------------------------------------------------------------ */}
      {totalCount > 0 && (
        <div className="relative h-6 flex items-center">
          {/* Track */}
          <div className="absolute inset-x-0 h-1.5 bg-gray-200 rounded-full" />

          {/* Dots */}
          {pauseAndGos.map((pag) => {
            const pct = totalMs > 0 ? (pag.time / totalMs) * 100 : 0
            const isCompleted = progress[pag.id]?.completed ?? false
            const dotColor = TIMELINE_DOT_COLORS[pag.type] ?? 'bg-gray-400'

            return (
              <button
                key={pag.id}
                type="button"
                title={`${formatTime(pag.time)} — ${PAUSE_AND_GO_TYPE_LABELS[pag.type]}`}
                onClick={() => onSeekTo(pag.time)}
                style={{ left: `${Math.min(Math.max(pct, 0), 100)}%` }}
                className={[
                  'absolute -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white transition-transform hover:scale-125 cursor-pointer',
                  dotColor,
                  isCompleted ? 'ring-2 ring-offset-1 ring-gray-400' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            )
          })}
        </div>
      )}

      {/* Type legend */}
      {totalCount > 0 && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(PAUSE_AND_GO_TYPE_LABELS).map(([type, label]) => {
            const hasItems = pauseAndGos.some((p) => p.type === type)
            if (!hasItems) return null
            return (
              <span key={type} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full ${TIMELINE_DOT_COLORS[type] ?? 'bg-gray-400'}`}
                />
                {label}
              </span>
            )
          })}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Empty state                                                         */}
      {/* ------------------------------------------------------------------ */}
      {totalCount === 0 && (
        <div className="py-12 text-center text-sm text-gray-500">{labels.noData}</div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Table                                                               */}
      {/* ------------------------------------------------------------------ */}
      {totalCount > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-10">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {labels.time}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {labels.type}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {labels.bundleItem}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {labels.message}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {labels.published}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {labels.status}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pauseAndGos.map((pag, index) => {
                const entry = progress[pag.id]
                const isCompleted = entry?.completed ?? false
                const result = entry?.result
                const isCurrentBundle = !pag.bundleItem || pag.bundleItem === currentBundleItem
                const displayMessage = getDisplayMessage(pag)

                return (
                  <tr
                    key={pag.id}
                    className={[
                      'transition-colors hover:bg-gray-50',
                      isCurrentBundle ? '' : 'opacity-50',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {/* # */}
                    <td className="px-4 py-3 text-gray-400 font-mono tabular-nums">
                      {index + 1}
                    </td>

                    {/* 시간 — clickable */}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => onSeekTo(pag.time)}
                        className="font-mono text-blue-600 underline underline-offset-2 cursor-pointer hover:text-blue-800 transition-colors"
                      >
                        {formatTime(pag.time)}
                      </button>
                    </td>

                    {/* 타입 — colored badge */}
                    <td className="px-4 py-3">
                      <span
                        className={[
                          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          PAUSE_AND_GO_TYPE_COLORS[pag.type] ?? 'bg-gray-100 text-gray-800',
                        ].join(' ')}
                      >
                        {PAUSE_AND_GO_TYPE_LABELS[pag.type]}
                      </span>
                    </td>

                    {/* 번들 */}
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                      {pag.bundleItem ?? '-'}
                    </td>

                    {/* 메시지 */}
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs max-w-[200px] truncate">
                      {displayMessage}
                    </td>

                    {/* 발행 상태 */}
                    <td className="px-4 py-3 text-center">
                      {pag.published ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {labels.published}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          {labels.unpublished}
                        </span>
                      )}
                    </td>

                    {/* 상태 */}
                    <td className="px-4 py-3">
                      {isCompleted ? (
                        <span className="inline-flex items-center gap-1.5 text-green-700">
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                          <span className="text-xs font-medium">
                            {result ? RESULT_LABELS[result] ?? result : labels.completed}
                          </span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-gray-400">
                          <Circle className="w-4 h-4 shrink-0" />
                          <span className="text-xs">{labels.notCompleted}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default PauseAndGoList
